import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/cn.js';

/**
 * Form controls.
 *
 * `Field` owns the id/aria wiring so every control is labelled and every error
 * is announced without each screen having to remember. Getting this wrong is
 * invisible to us and completely blocking for someone using a screen reader.
 */

interface FieldContextValue {
  id: string;
  errorId: string;
  hintId: string;
  hasError: boolean;
  hasHint: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

function useFieldContext() {
  return useContext(FieldContext);
}

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const ctx: FieldContextValue = {
    id,
    errorId: `${id}-error`,
    hintId: `${id}-hint`,
    hasError: !!error,
    hasHint: !!hint,
  };

  return (
    <FieldContext.Provider value={ctx}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label ? (
          <label htmlFor={id} className="text-sm font-medium text-foreground">
            {label}
            {required ? (
              <span className="ml-1 text-danger" aria-hidden>
                *
              </span>
            ) : null}
          </label>
        ) : null}
        {children}
        {hint && !error ? (
          <p id={ctx.hintId} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={ctx.errorId} className="flex items-center gap-1.5 text-xs font-medium text-danger">
            <AlertCircle className="size-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

const CONTROL_BASE = cn(
  'w-full rounded-lg border bg-surface px-3 text-sm text-foreground',
  'placeholder:text-subtle-foreground',
  'transition-[border-color,box-shadow] duration-150',
  'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/25',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-muted-foreground',
);

/** Wire a control to its Field's id and aria-describedby. */
function useControlProps(explicitId?: string) {
  const ctx = useFieldContext();
  if (!ctx) return { id: explicitId };
  const describedBy =
    [ctx.hasError ? ctx.errorId : null, ctx.hasHint && !ctx.hasError ? ctx.hintId : null]
      .filter(Boolean)
      .join(' ') || undefined;
  return {
    id: explicitId ?? ctx.id,
    'aria-invalid': ctx.hasError || undefined,
    'aria-describedby': describedBy,
  };
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, id, ...props }, ref) {
    const wired = useControlProps(id);
    const ctx = useFieldContext();
    return (
      <input
        ref={ref}
        className={cn(CONTROL_BASE, 'h-9.5', ctx?.hasError && 'border-danger', className)}
        {...wired}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, id, rows = 3, ...props }, ref) {
    const wired = useControlProps(id);
    const ctx = useFieldContext();
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(CONTROL_BASE, 'py-2 leading-relaxed', ctx?.hasError && 'border-danger', className)}
        {...wired}
        {...props}
      />
    );
  },
);

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
}

export interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  placeholder?: string;
}

/**
 * A native <select>, deliberately.
 *
 * On a phone this opens the OS picker - a big, familiar, one-thumb wheel that
 * works with gloves and never traps focus. A custom listbox looks better in a
 * screenshot and is worse on the floor.
 */
export const Select = forwardRef<HTMLSelectElement, NativeSelectProps>(function Select(
  { className, id, options, placeholder, ...props },
  ref,
) {
  const wired = useControlProps(id);
  const ctx = useFieldContext();
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          CONTROL_BASE,
          'h-9.5 appearance-none pr-9',
          ctx?.hasError && 'border-danger',
          className,
        )}
        {...wired}
        {...props}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.hint ? `${o.label} · ${o.hint}` : o.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
});

/** A checkbox with its label, for the handful of boolean fields in the catalog. */
export const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }
>(function Checkbox({ className, label, description, id, ...props }, ref) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <div className="flex items-start gap-2.5">
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        className={cn(
          'mt-0.5 size-4 shrink-0 rounded border-border-strong text-primary',
          'focus:ring-2 focus:ring-primary/25 focus:ring-offset-0',
          className,
        )}
        {...props}
      />
      <div className="min-w-0">
        <label htmlFor={inputId} className="text-sm font-medium text-foreground">
          {label}
        </label>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
    </div>
  );
});
